import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Typography, Card, CardContent, Chip, Button, CircularProgress, Avatar,
} from '@mui/material';
import {
  Notifications as NotifIcon, LocalHospital as ClinicIcon, CheckCircle as ReadIcon,
  DoneAll as ReadAllIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const typeColors = {
  clinic_registered: 'success',
  vitals_pending: 'warning',
  appointment_scheduled: 'info',
  note_ready: 'primary',
  invite: 'secondary',
  patient_assigned: 'info',
  queue_added: 'primary',
  your_turn: 'success',
  appointment_requested: 'warning',
  appointment_declined: 'error',
  report_added: 'primary',
};

const typeLabels = {
  clinic_registered: 'Registered',
  vitals_pending: 'Vitals Pending',
  appointment_scheduled: 'Appointment',
  note_ready: 'Note Ready',
  invite: 'Welcome',
  patient_assigned: 'Doctor Assigned',
  queue_added: 'In Queue',
  your_turn: 'Your Turn',
  appointment_requested: 'Request Sent',
  appointment_declined: 'Appointment',
  report_added: 'Report',
};

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadNotifications = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    try {
      const res = await axios.get(`${API_URL}/api/v1/patient/notifications`);
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
      if (silent) setRefreshing(false);
    }
  };

  const handleRefresh = () => loadNotifications({ silent: true });

  useEffect(() => { loadNotifications(); }, []);

  const markRead = async (id, { refresh = true } = {}) => {
    try {
      await axios.put(`${API_URL}/api/v1/patient/notifications/${id}/read`);
      if (refresh) loadNotifications();
    } catch {}
  };

  // Clicking a notification marks it read and nothing else. It used to navigate —
  // usually to the clinics list, which had nothing to do with what was announced —
  // so a glance at your notifications threw you off the page you were on.
  const handleCardClick = (n) => {
    if (!n.is_read) markRead(n.id);
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/v1/patient/notifications/read-all`);
      loadNotifications();
      toast.success('All notifications marked as read');
    } catch {}
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago · ${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`;
    return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}><NotifIcon /></Avatar>
          <Box>
            <Typography variant="h4" fontWeight={700}
              sx={{ fontFamily: '"Cormorant Garamond", Georgia, serif', color: '#431407' }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Typography variant="body2" color="text.secondary">
                {unreadCount} unread
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={
              refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
            }
            onClick={handleRefresh}
            variant="outlined"
            size="small"
            disabled={refreshing}
          >
            Refresh
          </Button>
          {unreadCount > 0 && (
            <Button startIcon={<ReadAllIcon />} onClick={markAllRead} variant="outlined" size="small">
              Mark all read
            </Button>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : notifications.length === 0 ? (
        <Card sx={{ background: 'linear-gradient(145deg, #fffefb 0%, #faf6f0 100%)', maxWidth: 480, mx: 'auto' }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <NotifIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No notifications yet</Typography>
            <Typography variant="body2" color="text.secondary">
              You'll see updates here when your clinic registers you or your visit notes are ready.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {notifications.map((n) => (
            <Card key={n.id} onClick={() => handleCardClick(n)} sx={{
              opacity: n.is_read ? 0.7 : 1,
              borderLeft: n.is_read ? 'none' : '4px solid #b45309',
              background: n.is_read
                ? 'linear-gradient(135deg, #faf6f0 0%, #f5ebe0 100%)'
                : 'linear-gradient(135deg, #fffefb 0%, #fff7ed 100%)',
              transition: 'opacity 0.3s, box-shadow 0.2s',
              cursor: n.is_read ? 'default' : 'pointer',
              '&:hover': n.is_read ? undefined : { boxShadow: 3 },
            }}>
              <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40 }}>
                  <ClinicIcon fontSize="small" />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={700}>{n.title}</Typography>
                    <Chip size="small" label={typeLabels[n.type] || n.type}
                      color={typeColors[n.type] || 'default'} variant="outlined" />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                      {formatDate(n.created_at)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">{n.message}</Typography>
                  {n.clinic_name && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {n.clinic_name}
                    </Typography>
                  )}
                </Box>
                {n.is_read ? (
                  <Chip size="small" label="Read" icon={<ReadIcon fontSize="small" />}
                    variant="outlined" sx={{ color: 'text.disabled' }} />
                ) : (
                  <Button
                    size="small"
                    startIcon={<ReadIcon fontSize="small" />}
                    onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                    sx={{ flexShrink: 0 }}
                  >
                    Mark read
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}
